import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { DialogService } from './dialog.service';

export interface Permission {
  moduleCode: string; moduleName: string; routePath: string; moduleGroup: string;
  canView: boolean; canAdd: boolean; canEdit: boolean; canDelete: boolean;
}
export interface SessionUser {
  userId: number; username: string; email: string; fullName: string; firstName: string;
  roleCode: string; roleName: string; organizationId: number; orgName: string; branchId: number; branchName: string;
}
export interface Session {
  token: string; mustChangePassword: boolean; dateFormat: string; financialYear: string;
  unreadNotifications: number; user: SessionUser; permissions: Permission[];
}

const KEY = 'rb_session';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private sessionSubject = new BehaviorSubject<Session>(this.read());
  session$ = this.sessionSubject.asObservable();

  constructor(private api: ApiService, private router: Router, private dialog: DialogService) {}

  get session(): Session { return this.sessionSubject.value; }
  get token(): string { return this.session ? this.session.token : null; }
  get user(): SessionUser { return this.session ? this.session.user : null; }
  get isLoggedIn(): boolean { return !!this.token; }
  get isSuperAdmin(): boolean { return !!this.user && this.user.roleCode === 'SUPER_ADMIN'; }
  get isAdmin(): boolean { return !!this.user && (this.user.roleCode === 'SUPER_ADMIN' || this.user.roleCode === 'ADMIN'); }
  get dateFormat(): string { return (this.session && this.session.dateFormat) || 'dd/MM/yyyy'; }

  can(moduleCode: string, right: 'view' | 'add' | 'edit' | 'delete' = 'view'): boolean {
    if (!this.session) { return false; }
    if (this.isSuperAdmin) { return true; }
    const p = this.session.permissions.find(x => x.moduleCode === moduleCode);
    if (!p) { return false; }
    return right === 'view' ? p.canView : right === 'add' ? p.canAdd : right === 'edit' ? p.canEdit : p.canDelete;
  }

  login(username: string, password: string): Observable<Session> {
    return this.api.post<Session>('/auth/login', { username, password }).pipe(tap(s => this.store(s)));
  }

  /** Refreshes permissions / date format after settings change or page reload. */
  refresh(): Observable<Session> {
    return this.api.get<Session>('/auth/me').pipe(tap(s => this.store(s)));
  }

  /** Asks first — used by every Logout / Sign Out button. */
  askLogout(): void {
    this.dialog.confirm({
      icon: '⎋', title: 'Log out?', message: 'Are you sure you want to log out of Resume Builder?',
      okText: 'Yes, log out', cancelText: 'Stay signed in'
    }).then(ok => { if (ok) { this.logout(); } });
  }

  logout(callServer = true): void {
    if (callServer && this.token) { this.api.post('/auth/logout').subscribe({ error: () => {} }); }
    localStorage.removeItem(KEY);
    this.sessionSubject.next(null);
    this.router.navigate(['/signin']);
  }

  patch(changes: Partial<Session>): void { this.store(Object.assign({}, this.session, changes)); }

  private store(s: Session): void {
    localStorage.setItem(KEY, JSON.stringify(s));
    this.sessionSubject.next(s);
  }

  private read(): Session {
    try { return JSON.parse(localStorage.getItem(KEY)); } catch (e) { return null; }
  }
}
